import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import IpatientsTable from '../model/patientTable'

export interface EncountersFilters {
  draftSchedule: string[]
  draftFromDate: string
  draftToDate: string
  draftHospitals: string[]
  draftSearch: string
  appliedSchedule: string[]
  appliedFromDate: string
  appliedToDate: string
  appliedHospitals: string[]
  searchQuery: string
  hasFetched: boolean
  shouldRestoreState: boolean
  encounters: any[]
  page: number
  showing: number
  total: number
  scrollPosition: number
  sortBy: string
  sortDir: string
  lastSync: string | null
}

export interface commonDataState {
  selectedStage: string
  selectedProgram: string[]
  programFilterVersion: number
  recommended_calculators: string[]
  batch_groups: { mapping: Record<string, any>, groups: Record<string, any> }
  eig_ranking: string[]
  selectedPatient: string
  patientList: string[]
  patientTotal: {
    total: ''
    showing: ''
  }
  patientsTable: IpatientsTable
  clinicVisitsTable: IpatientsTable
  encountersFilters: EncountersFilters
  loggedInUser: any
  encryptedToken: { token: string; status: string }
  errorInfo: { msg: string; status: string }
  carePlanRejected: boolean
  selectedPU: { name: ''; does: ''; unit: ''; fq: ''; rxcui: '' }
  selectedSchedule: any
  selectedCarePlan: any
  otherConditions: any
  ICD10Codes: any
  ClinicalScores: any
  monitors: any
  lifestyles: any
  programFilter: any
  programChips: any
  selectedTask: { prg: ''; isUpdate: false }
}

const initialPatientsTable = {
  limit: 50,
  page: 1,
  sort: '',
  search: '',
  programs: '',
  hospitals: localStorage.getItem('atman-hospitals') || '',
  status: '',
  gender: '',
  stage: '',
  state: '',
  priority: '',
  inactive: 'false',
  activity_days: '',
  approval_status: 'false',
  clinic_visit_status: '',
  cardiologist: '',
  clinic_visit_days: '',
  isSubmit: '',
  scrollPosition: 0,
  preserveData: '',
  shouldRestoreState: 'false',
}
const initialEncountersFilters: EncountersFilters = {
  draftSchedule: ['today', 'tomorrow', 'next_7_days'],
  draftFromDate: '',
  draftToDate: '',
  draftHospitals: [],
  draftSearch: '',
  appliedSchedule: ['today', 'tomorrow', 'next_7_days'],
  appliedFromDate: '',
  appliedToDate: '',
  appliedHospitals: [],
  searchQuery: '',
  hasFetched: false,
  shouldRestoreState: false,
  encounters: [],
  page: 1,
  showing: 0,
  total: 0,
  scrollPosition: 0,
  sortBy: 'visit_date',
  sortDir: 'desc',
  lastSync: null,
}

const initialState: commonDataState = {
  selectedStage: '',
  selectedProgram: [],
  programFilterVersion: 0,
  recommended_calculators: [],
  batch_groups: { mapping: {}, groups: {} },
  eig_ranking: [],
  selectedPatient: '',
  patientList: [] as any,
  patientTotal: {
    total: '',
    showing: '',
  },
  patientsTable: initialPatientsTable,
  clinicVisitsTable: { ...initialPatientsTable, hospitals: '' },
  encountersFilters: initialEncountersFilters,
  loggedInUser: { roles: [], email: '' },
  encryptedToken: { token: '', status: '' },
  errorInfo: { msg: '', status: '' },
  carePlanRejected: false,
  selectedPU: { name: '', does: '', unit: '', fq: '', rxcui: '' },
  selectedSchedule: {},
  selectedCarePlan: {},
  otherConditions: [],
  ICD10Codes: [],
  ClinicalScores: [],
  monitors: [],
  lifestyles: [],
  programFilter: [],
  programChips: [],
  selectedTask: { prg: '', isUpdate: false },
}

export const commonDataSlice = createSlice({
  name: 'common_data',
  initialState,
  reducers: {
    setSelectedStage: (state, action: PayloadAction<any>) => {
      state.selectedStage = action.payload
    },
    setSelectedProgram: (state, action: PayloadAction<any>) => {
      state.selectedProgram = action.payload
    },
    setRecommendedCalculators: (state, action: PayloadAction<any>) => {
      state.recommended_calculators = action.payload
    },
    setBatchGroups: (state, action: PayloadAction<any>) => {
      state.batch_groups = action.payload
    },
    setEigRanking: (state, action: PayloadAction<string[]>) => {
      state.eig_ranking = action.payload
    },
    setSelectedPatient: (state, action: PayloadAction<any>) => {
      state.selectedPatient = action.payload
    },
    setPatientList: (state, action) => {
      state.patientList = action.payload
    },
    setPatientTotal: (state, action) => {
      state.patientTotal = action.payload
    },
    setPatientsTable: (state, action: PayloadAction<any>) => {
      state.patientsTable = action.payload
    },
    setClinicVisitsTable: (state, action: PayloadAction<any>) => {
      state.clinicVisitsTable = action.payload
    },
    setEncountersFilters: (state, action: PayloadAction<Partial<EncountersFilters>>) => {
      state.encountersFilters = { ...state.encountersFilters, ...action.payload }
    },
    resetEncountersFilters: (state) => {
      state.encountersFilters = initialEncountersFilters
    },
    setLoggedInUser: (state, action: PayloadAction<any>) => {
      state.loggedInUser = action.payload
    },
    setEncryptedToken: (state, action: PayloadAction<any>) => {
      state.encryptedToken = action.payload
    },
    setErrorInfo: (state, action: PayloadAction<any>) => {
      state.errorInfo = action.payload
    },
    setCarePlanRejected: (state, action: PayloadAction<any>) => {
      state.carePlanRejected = action.payload
    },
    setSelectedPU: (state, action: PayloadAction<any>) => {
      state.selectedPU = action.payload
    },
    setSelectedSchedule: (state, action: PayloadAction<any>) => {
      state.selectedSchedule = action.payload
    },
    setSelectedCarePlan: (state, action: PayloadAction<any>) => {
      state.selectedCarePlan = action.payload
    },
    setMonitors: (state, action: PayloadAction<any>) => {
      state.monitors = action.payload
    },
    setLifestyles: (state, action: PayloadAction<any>) => {
      state.lifestyles = action.payload
    },
    setOtherConditions: (state, action: PayloadAction<any>) => {
      state.otherConditions = action.payload
    },
    setICD10Codes: (state, action: PayloadAction<any>) => {
      state.ICD10Codes = action.payload
    },
    setClinicalScores: (state, action: PayloadAction<any>) => {
      state.ClinicalScores = action.payload
    },
    setProgramFilter: (state, action: PayloadAction<any>) => {
      state.programFilter = action.payload
    },
    setProgramChips: (state, action: PayloadAction<any>) => {
      state.programChips = action.payload
    },
    incrementProgramFilterVersion: (state) => {
      state.programFilterVersion += 1
    },
    resetSelectedProgram: (state) => {
      state.selectedProgram = initialState.selectedProgram
      state.programFilterVersion = 0
    },
    resetSelectedPatient: (state) => {
      state.selectedPatient = ''
    },
    resetSelectedStage: (state) => {
      state.selectedStage = initialState.selectedStage
    },
    resetPatientsTable: (state) => {
      state.patientsTable = initialState.patientsTable
    },
    resetLoggedInUser: (state) => {
      state.loggedInUser = initialState.loggedInUser
    },
    resetEncryptedToken: (state) => {
      state.encryptedToken = initialState.encryptedToken
    },
    resetErrorInfo: (state) => {
      state.errorInfo = initialState.errorInfo
    },
    resetCarePlanRejected: (state) => {
      state.carePlanRejected = initialState.carePlanRejected
    },
    resetSelectedPU: (state) => {
      state.selectedPU = initialState.selectedPU
    },
    setSelectedTask: (state, action: PayloadAction<any>) => {
      state.selectedTask = action.payload
    },
  },
})

export const {
  setSelectedStage,
  setSelectedProgram,
  setRecommendedCalculators,
  setSelectedPatient,
  resetSelectedPatient,
  resetSelectedProgram,
  setPatientsTable,
  setClinicVisitsTable,
  resetSelectedStage,
  resetPatientsTable,
  setLoggedInUser,
  resetLoggedInUser,
  setEncryptedToken,
  resetEncryptedToken,
  setErrorInfo,
  resetErrorInfo,
  setCarePlanRejected,
  resetCarePlanRejected,
  setSelectedPU,
  resetSelectedPU,
  setSelectedSchedule,
  setSelectedCarePlan,
  setOtherConditions,
  setICD10Codes,
  setClinicalScores,
  setMonitors,
  setLifestyles,
  setProgramFilter,
  setProgramChips,
  setBatchGroups,
  setEigRanking,
  setSelectedTask,
  setPatientList,
  setPatientTotal,
  setEncountersFilters,
  resetEncountersFilters,
  incrementProgramFilterVersion,
} = commonDataSlice.actions
export default commonDataSlice.reducer